// The form for one supplier agreement (SupplierContract), shared by the supplier panel's Contracts tab and the
// RoPA form's "Add a processing agreement". It only ever writes through the supplier register's own contract
// routes (POST / PUT /tprm/suppliers/:id/contracts, tprm:write on the server), so the dates, the terms and the
// restore commitment are checked by the same rules wherever an agreement is recorded from.
//
// Each term is three-state: in the agreement, answered and not in it, or not answered. An unanswered term is sent
// absent and is never counted as present (backend lib/supplierContracts.ts; docs/TPRM-CONTRACTS.md).

import { useEffect, useState } from "react";
import { api, apiErrorMessage } from "../api/client";
import { theme, btnPrimary, inputStyle } from "../theme";
import {
  CONTRACT_KINDS, CONTRACT_KIND_LABELS, CONTRACT_STATUSES, termsRequiredFor, withLegacyTerms,
  type ContractRelationship,
} from "../lib/supplierContracts";

/** The stored agreement as the API returns it (GET /tprm/suppliers/:id/contracts). */
export interface SupplierContractRow {
  id: string;
  title: string;
  reference: string | null;
  kind: string;
  status: string;
  startDate: string | null;
  endDate: string | null;
  autoRenew: boolean;
  noticePeriodDays: number | null;
  reviewDate: string | null;
  owner: string | null;
  annualValue: number | null;
  currency: string | null;
  terms: Record<string, boolean> | null;
  incidentNotificationHours: number | null;
  restoreCommitted?: boolean | null;
  restoreTimeHours?: number | null;
  restorePointHours?: number | null;
  dataLocations: string[];
  documentUrl: string | null;
  notes: string | null;
}

export const emptyContract = {
  title: "", reference: "", kind: "msa" as string, status: "active" as string,
  startDate: "", endDate: "", autoRenew: false, noticePeriodDays: "", reviewDate: "",
  owner: "", annualValue: "", currency: "GBP", documentUrl: "", notes: "",
  incidentNotificationHours: "", dataLocations: "", terms: {} as Record<string, boolean>,
  // Restore commitment: "" = not answered, "no" = none, "yes" = restoreTimeHours (and restorePointHours where stated).
  restoreCommitted: "" as "" | "yes" | "no", restoreTimeHours: "", restorePointHours: "",
};
export type ContractForm = typeof emptyContract;

export const toContractForm = (c: SupplierContractRow): ContractForm => ({
  title: c.title, reference: c.reference ?? "", kind: c.kind, status: c.status,
  startDate: c.startDate ? c.startDate.slice(0, 10) : "", endDate: c.endDate ? c.endDate.slice(0, 10) : "",
  autoRenew: c.autoRenew, noticePeriodDays: c.noticePeriodDays?.toString() ?? "",
  reviewDate: c.reviewDate ? c.reviewDate.slice(0, 10) : "", owner: c.owner ?? "",
  annualValue: c.annualValue?.toString() ?? "", currency: c.currency ?? "GBP",
  documentUrl: c.documentUrl ?? "", notes: c.notes ?? "",
  incidentNotificationHours: c.incidentNotificationHours?.toString() ?? "",
  dataLocations: (c.dataLocations ?? []).join(", "), terms: withLegacyTerms(c.terms),
  restoreCommitted: c.restoreCommitted === true ? "yes" : c.restoreCommitted === false ? "no" : "",
  restoreTimeHours: c.restoreTimeHours?.toString() ?? "", restorePointHours: c.restorePointHours?.toString() ?? "",
});

/** What the contract routes take. Date-only fields travel as UTC midnight. */
export function contractBody(f: ContractForm): Record<string, unknown> {
  return {
    title: f.title, reference: f.reference || null, kind: f.kind, status: f.status,
    startDate: f.startDate ? new Date(f.startDate).toISOString() : null,
    endDate:   f.endDate   ? new Date(f.endDate).toISOString()   : null,
    autoRenew: f.autoRenew,
    noticePeriodDays: f.noticePeriodDays === "" ? null : Number(f.noticePeriodDays),
    reviewDate: f.reviewDate ? new Date(f.reviewDate).toISOString() : null,
    owner: f.owner || null,
    annualValue: f.annualValue === "" ? null : Number(f.annualValue),
    currency: f.currency || "GBP",
    // Only answered terms are sent; an unanswered one stays absent (never present).
    terms: f.terms,
    incidentNotificationHours: f.incidentNotificationHours === "" ? null : Number(f.incidentNotificationHours),
    restoreCommitted: f.restoreCommitted === "yes" ? true : f.restoreCommitted === "no" ? false : null,
    restoreTimeHours:  f.restoreCommitted === "yes" && f.restoreTimeHours  !== "" ? Number(f.restoreTimeHours)  : null,
    restorePointHours: f.restoreCommitted === "yes" && f.restorePointHours !== "" ? Number(f.restorePointHours) : null,
    dataLocations: f.dataLocations.split(",").map((x) => x.trim()).filter(Boolean),
    documentUrl: f.documentUrl || "", notes: f.notes || null,
  };
}

/** A term's answer as a select value: "yes" in the agreement, "no" not in it, "" not answered. */
const termValue = (terms: Record<string, boolean>, key: string) => (terms[key] === true ? "yes" : terms[key] === false ? "no" : "");

function withTerm(terms: Record<string, boolean>, key: string, v: string): Record<string, boolean> {
  const next = { ...terms };
  if (v === "") delete next[key];
  else next[key] = v === "yes";
  return next;
}

interface CandidateDocument { id: string; fileName: string; version: number }

export function SupplierContractForm({ supplierId, contractId, initial, rel, onSaved, onCancel, processorFirst = false, offerSignedCopy = false, kinds }: {
  supplierId: string;
  /** null records a new agreement. */
  contractId: string | null;
  initial: ContractForm;
  rel: ContractRelationship;
  /** `warning` says what did not happen after the agreement itself was saved (the signed copy link). */
  onSaved: (contractId: string | null, warning?: string) => void;
  onCancel: () => void;
  /** List the Art. 28(3) processor terms first (the RoPA flow). */
  processorFirst?: boolean;
  /** Offer the Evidence Repository document that holds the signed copy (Documents tier); linked after saving. */
  offerSignedCopy?: boolean;
  /** Narrow the kinds offered (the RoPA flow records a data processing agreement). */
  kinds?: readonly string[];
}) {
  const [form, setFormState] = useState<ContractForm>(initial);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [documents, setDocuments] = useState<CandidateDocument[] | null>(null);
  const [signedCopyId, setSignedCopyId] = useState("");
  const set = (p: Partial<ContractForm>) => setFormState((f) => ({ ...f, ...p }));

  // The signed copy is an Evidence Repository document; the list sits behind the Documents tier, so it is
  // simply not offered where the organisation cannot open that tier.
  useEffect(() => {
    if (!offerSignedCopy) return;
    api.get<{ documents: CandidateDocument[] }>("/tprm/signed-copy-candidates")
      .then((d) => setDocuments(d.documents ?? []))
      .catch(() => setDocuments(null));
  }, [offerSignedCopy]);

  const terms = termsRequiredFor(rel);
  const ordered = processorFirst ? [...terms.filter((t) => t.scope === "processor"), ...terms.filter((t) => t.scope !== "processor")] : terms;
  const kindOptions = CONTRACT_KINDS.filter((k) => !kinds || kinds.includes(k) || k === form.kind);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    // React events cross portals: opened from the RoPA form, this submit must not also submit the activity.
    e.stopPropagation();
    setSaving(true); setError(null);
    try {
      const body = contractBody(form);
      let savedId: string | null = contractId;
      if (contractId) {
        await api.put(`/tprm/suppliers/${supplierId}/contracts/${contractId}`, body);
      } else {
        const r = await api.post<{ contract?: { id: string } }>(`/tprm/suppliers/${supplierId}/contracts`, body);
        savedId = r?.contract?.id ?? null;
      }
      if (savedId && signedCopyId) {
        try { await api.put(`/tprm/suppliers/${supplierId}/contracts/${savedId}/signed-copy`, { documentId: signedCopyId }); }
        catch (err) {
          // The agreement is saved (a second submit would record it twice): close and say what did not happen.
          onSaved(savedId, `The agreement was saved, but the signed copy was not linked: ${apiErrorMessage(err, "it could not be linked")}. Link it from the supplier's Contracts tab.`);
          return;
        }
      }
      onSaved(savedId);
    } catch (err) { setError(apiErrorMessage(err, "Could not save the agreement")); }
    finally { setSaving(false); }
  }

  const label: React.CSSProperties = { fontSize: 12, display: "grid", gap: 4 };
  const input: React.CSSProperties = { ...inputStyle, fontSize: 13 };

  return (
    <form onSubmit={submit} style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, padding: 14, border: "1px solid var(--c-border)", borderRadius: 3 }}>
      <label style={{ ...label, gridColumn: "1 / -1" }}>Title
        <input required value={form.title} onChange={(e) => set({ title: e.target.value })} placeholder="Master services agreement 2026–2029" style={input} />
      </label>
      <label style={label}>Type
        <select value={form.kind} onChange={(e) => set({ kind: e.target.value })} style={input}>
          {kindOptions.map((k) => <option key={k} value={k}>{CONTRACT_KIND_LABELS[k]}</option>)}
        </select>
      </label>
      <label style={label}>Status
        <select value={form.status} onChange={(e) => set({ status: e.target.value })} style={input}>
          {CONTRACT_STATUSES.map((k) => <option key={k} value={k}>{k === "draft" ? "Draft — not signed" : k === "active" ? "Signed and in force" : k === "expired" ? "Expired" : "Terminated"}</option>)}
        </select>
      </label>
      <label style={label}>Contract reference<input value={form.reference} onChange={(e) => set({ reference: e.target.value })} style={input} /></label>
      <label style={label}>Agreement owner<input value={form.owner} onChange={(e) => set({ owner: e.target.value })} placeholder="Who decides on renewal" style={input} /></label>
      <label style={label}>Start date<input type="date" value={form.startDate} onChange={(e) => set({ startDate: e.target.value })} style={input} /></label>
      <label style={label}>End date<input type="date" value={form.endDate} onChange={(e) => set({ endDate: e.target.value })} style={input} /></label>
      <label style={label}>Notice period (days)<input type="number" min={0} value={form.noticePeriodDays} onChange={(e) => set({ noticePeriodDays: e.target.value })} placeholder="90" style={input} /></label>
      <label style={label}>Next commercial review<input type="date" value={form.reviewDate} onChange={(e) => set({ reviewDate: e.target.value })} style={input} /></label>
      <label style={{ fontSize: 12, display: "flex", gap: 8, alignItems: "center" }}><input type="checkbox" checked={form.autoRenew} onChange={(e) => set({ autoRenew: e.target.checked })} />Renews automatically unless notice is given</label>
      <label style={label}>Annual value<input type="number" min={0} value={form.annualValue} onChange={(e) => set({ annualValue: e.target.value })} style={input} /></label>
      <label style={label}>Currency<input value={form.currency} maxLength={3} onChange={(e) => set({ currency: e.target.value.toUpperCase() })} style={input} /></label>
      <label style={label}>Supplier must report an incident within (hours)<input type="number" min={0} value={form.incidentNotificationHours} onChange={(e) => set({ incidentNotificationHours: e.target.value })} placeholder="24" style={input} /></label>
      <label style={label}>Commits to a restore time?<select value={form.restoreCommitted} onChange={(e) => set({ restoreCommitted: e.target.value as ContractForm["restoreCommitted"] })} style={input}><option value="">Not answered</option><option value="yes">Yes</option><option value="no">No restore commitment</option></select></label>
      {form.restoreCommitted === "yes" && (<>
        <label style={label}>Restores the service within (hours)<input type="number" min={0} required value={form.restoreTimeHours} onChange={(e) => set({ restoreTimeHours: e.target.value })} placeholder="4" title="Compared with the RTO of every business impact analysis that depends on this supplier" style={input} /></label>
        <label style={label}>Loses at most (hours of data)<input type="number" min={0} value={form.restorePointHours} onChange={(e) => set({ restorePointHours: e.target.value })} placeholder="Leave empty if not stated" title="Compared with the RPO of every business impact analysis that depends on this supplier" style={input} /></label>
      </>)}
      <label style={label}>Data may be held in<input value={form.dataLocations} onChange={(e) => set({ dataLocations: e.target.value })} placeholder="United Kingdom, Ireland" style={input} /></label>
      {offerSignedCopy && documents !== null && (
        <label style={{ ...label, gridColumn: "1 / -1" }}>Signed copy (Evidence Repository)
          <select value={signedCopyId} onChange={(e) => setSignedCopyId(e.target.value)} style={input}>
            <option value="">None linked yet</option>
            {documents.map((d) => <option key={d.id} value={d.id}>{d.fileName} (v{d.version})</option>)}
          </select>
          <span style={{ fontSize: 11, color: theme.color.muted }}>Upload the signed agreement under Documents first; its later versions follow it.</span>
        </label>
      )}
      <label style={{ ...label, gridColumn: "1 / -1" }}>Link to the signed copy (https)<input value={form.documentUrl} onChange={(e) => set({ documentUrl: e.target.value })} placeholder="https://" style={input} /></label>
      <label style={{ ...label, gridColumn: "1 / -1" }}>Notes<textarea rows={2} value={form.notes} onChange={(e) => set({ notes: e.target.value })} style={{ ...input, resize: "vertical" }} /></label>

      <div style={{ gridColumn: "1 / -1" }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: theme.color.heading, marginBottom: 2 }}>What the agreement carries</div>
        <div style={{ fontSize: 11, color: theme.color.muted, marginBottom: 8 }}>Answer a term only from the agreement itself. A term left "Not answered" is reported as not checked, never as present.</div>
        <div style={{ display: "grid", gap: 6 }}>
          {ordered.map((t) => (
            <div key={t.key} style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) 170px", gap: 10, alignItems: "center" }} title={`${t.detail} (${t.basis})`}>
              <span style={{ fontSize: 12, lineHeight: 1.5 }}><strong style={{ fontWeight: 600 }}>{t.label}</strong> <span style={{ color: theme.color.muted }}>— {t.basis}</span></span>
              <select aria-label={t.label} value={termValue(form.terms, t.key)} onChange={(e) => set({ terms: withTerm(form.terms, t.key, e.target.value) })} style={{ ...input, fontSize: 12 }}>
                <option value="">Not answered</option>
                <option value="yes">In the agreement</option>
                <option value="no">Not in the agreement</option>
              </select>
            </div>
          ))}
        </div>
      </div>

      {error && <div role="alert" style={{ gridColumn: "1 / -1", fontSize: 12, color: "var(--c-dangerText)" }}>{error}</div>}
      <div style={{ gridColumn: "1 / -1", display: "flex", gap: 8 }}>
        <button type="submit" disabled={saving} style={{ ...btnPrimary, fontSize: 13, opacity: saving ? 0.6 : 1 }}>{saving ? "Saving…" : contractId ? "Save changes" : "Save agreement"}</button>
        <button type="button" onClick={onCancel} style={{ padding: "8px 14px", fontSize: 13, borderRadius: 3, border: "1px solid var(--c-border)", background: "transparent", color: theme.color.muted, cursor: "pointer" }}>Cancel</button>
      </div>
    </form>
  );
}
